"use client";

import React, { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { getErrorMessage } from "@/types/problem-detail";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { share, listShares, revokeShare } from "@/services/shareService";
import type { CNSShareEntityType, CNSShareRole } from "@/models/cns";
import type { CNSShareListResponse } from "@/models/cns";

export interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: CNSShareEntityType;
  entityPublicId: string;
  entityLabel?: string;
  scopeDescription?: string;
  /** Optional link to widen or change share scope inside the dialog. */
  scopeSwitchLabel?: string;
  onScopeSwitch?: () => void;
  /** Called after the access list changes (invite sent or access revoked). */
  onAccessListChanged?: (memberCount: number) => void;
}

const ENTITY_LABELS: Record<CNSShareEntityType, string> = {
  project: "project",
  conversation: "conversation",
  workspace: "workspace",
};

export function ShareDialog({
  open,
  onOpenChange,
  entityType,
  entityPublicId,
  entityLabel,
  scopeDescription,
  scopeSwitchLabel,
  onScopeSwitch,
  onAccessListChanged,
}: ShareDialogProps) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<CNSShareRole>(
    entityType === "workspace" ? "member" : "editor"
  );
  const [submitting, setSubmitting] = useState(false);
  const [accessList, setAccessList] = useState<CNSShareListResponse | null>(null);
  const [accessListLoading, setAccessListLoading] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const titleLabel = entityLabel ?? ENTITY_LABELS[entityType];
  const title = `Share ${titleLabel}`;
  const scope = scopeDescription ?? "Only people you invite can access.";
  const showRoleSelect = entityType === "project" || entityType === "conversation";
  const isValid = email.trim().length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        setEmail("");
        setRole(entityType === "workspace" ? "member" : "editor");
        setAccessList(null);
      }
      onOpenChange(next);
    },
    [onOpenChange, entityType]
  );

  const loadAccessList = useCallback(async () => {
    if (!entityPublicId) return;
    setAccessListLoading(true);
    try {
      const data = await listShares(entityType, entityPublicId);
      setAccessList(data);
      onAccessListChanged?.(data.members.length);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load people with access."));
    } finally {
      setAccessListLoading(false);
    }
  }, [entityType, entityPublicId, onAccessListChanged]);

  useEffect(() => {
    if (open && entityPublicId) loadAccessList();
  }, [open, entityPublicId, entityType, loadAccessList]);

  const handleRevoke = async (id: string) => {
    if (revokingId) return;
    setRevokingId(id);
    try {
      await revokeShare(id);
      setAccessList((prev) => {
        if (!prev) return null;
        const next = {
          ...prev,
          members: prev.members.filter((member) => member.id !== id),
        };
        onAccessListChanged?.(next.members.length);
        return next;
      });
      toast.success("Access removed.");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to remove access."));
    } finally {
      setRevokingId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!entityPublicId || !isValid || submitting) return;
    setSubmitting(true);
    try {
      await share(entityType, entityPublicId, email.trim(), role);
      toast.success("Invitation sent.");
      await loadAccessList();
      setEmail("");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to share. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md gap-6">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Invite someone by email. They will get access according to the role you choose.
          </DialogDescription>
        </DialogHeader>
        {(accessListLoading || accessList) && (
          <div className="space-y-2 rounded-lg bg-muted/40 p-3">
            <h4 className="text-sm font-medium text-foreground">People with access</h4>
            {accessListLoading ? (
              <p className="text-muted-foreground text-sm">Loading…</p>
            ) : accessList ? (
              <ul className="space-y-2 text-sm">
                {accessList.owner && (
                  <li className="flex items-center justify-between gap-2 rounded-md border border-border bg-card p-2">
                    <div className="min-w-0 flex flex-col gap-0.5">
                      <span className="text-foreground whitespace-nowrap truncate">
                        {accessList.owner.name ?? "Owner"}
                      </span>
                      {accessList.owner.email && (
                        <span className="text-muted-foreground text-xs truncate">
                          {accessList.owner.email}
                        </span>
                      )}
                    </div>
                    <span className="text-muted-foreground shrink-0">Owner</span>
                  </li>
                )}
                {accessList.members.map((member) => (
                  <li
                    key={member.id}
                    className="flex items-center justify-between gap-2 rounded-md border border-border bg-card p-2"
                  >
                    <div className="min-w-0 flex flex-col gap-0.5">
                      <span className="text-foreground whitespace-nowrap truncate">
                        {member.name ?? member.email ?? "—"}
                      </span>
                      {member.name && member.email && (
                        <span className="text-muted-foreground text-xs truncate">
                          {member.email}
                        </span>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="text-muted-foreground capitalize">
                        {member.role}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 cursor-pointer border-destructive/50 text-destructive hover:bg-destructive/10 hover:border-destructive hover:text-destructive"
                        onClick={() => handleRevoke(member.id)}
                        disabled={revokingId !== null}
                      >
                        {revokingId === member.id ? "Removing…" : "Remove"}
                      </Button>
                    </div>
                  </li>
                ))}
                {accessList.members.length === 0 && (
                  <li className="text-muted-foreground">No one else has access yet.</li>
                )}
              </ul>
            ) : null}
          </div>
        )}
        <form
          onSubmit={handleSubmit}
          className={cn(
            "space-y-4",
            (accessListLoading || accessList) && "border-t border-border pt-4 mt-4"
          )}
        >
          <div className="space-y-2">
            <Label htmlFor="share-email" className="text-foreground">Email</Label>
            <Input
              id="share-email"
              type="email"
              placeholder="colleague@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              autoFocus
            />
          </div>
          {showRoleSelect && (
            <div className="space-y-2">
              <Label htmlFor="share-role" className="text-foreground">Role</Label>
              <Select
                value={role}
                onValueChange={(v) => setRole(v as CNSShareRole)}
                disabled={submitting}
              >
                <SelectTrigger id="share-role" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">Viewer</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {entityType === "workspace" && (
            <p className="text-muted-foreground text-sm">They will be added as a member.</p>
          )}
          {scopeSwitchLabel && onScopeSwitch ? (
            <Button
              type="button"
              variant="link"
              className="h-auto p-0 text-sm"
              onClick={onScopeSwitch}
            >
              {scopeSwitchLabel}
            </Button>
          ) : null}
          <p className="text-muted-foreground text-sm">{scope}</p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="default" disabled={!isValid || submitting}>
              {submitting ? "Sending…" : "Send invite"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
